import { defineComponent as _defineComponent } from 'vue'
export interface PanelConfig {
  tableKey: TablePanels.ModuleKey;
  loaderOptions?: Nullable<GenericObject>;
}

/** `CardTableGroup` component properties. */
export interface Props {
  /**
   * A mixed array of either table panel module keys, and/or {@link PanelConfig}
   * objects.
   */
  items: (TablePanels.ModuleKey | PanelConfig)[];
  /** ... */
  id?: string;
}

type ViewMode = 'single' | 'multi';


import { ref, computed, watchEffect } from 'vue';
import { kebabCase, startCase, sortBy } from 'lodash';

import { useStore, TablePanels } from '@store';
import { ls } from '@services/ls';
import { isString } from '@tools/type-guards';

import Icon from '@components/Icon.vue';

import { faList } from '@icons/solid/faList';
import { faGrid2 } from '@icons/solid/faGrid2';

import GroupItem from './CardTableGroupItem.vue';
import GroupView from './CardTableGroupView.vue';

const CACHE_ID_PREFIX = 'zephyr.cardTableGroup';

function titleCase(value: string) {
  return startCase(kebabCase(value).replace(/-/g, ' '));
}

declare module '@vue/runtime-core' {
  export interface GlobalComponents {
    CardTableGroup: ComponentWithProps<Props>;
  }
}

const __default__ = { name: 'CardTableGroup' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    items: null,
    id: null
  },
  setup(__props: any) {

const props = __props as {
  /**
   * A mixed array of either table panel module keys, and/or {@link PanelConfig}
   * objects.
   */
  items: (TablePanels.ModuleKey | PanelConfig)[];
  /** ... */
  id?: string;
};



const store = useStore();

/**
 * Filtered list of table panel configs based upon whether or not they're
 * enabled.
 */
const configs = computed(() => {
  const configs: PanelConfig[] = [];

  for (const item of props.items) {
    const config = isString(item) ? { tableKey: item } : item;

    if (!isPanelEnabled(config.tableKey)) continue;

    configs.push(config);
  }

  return configs;
});

/**
 * ...
 *
 * @param panel ...
 */
function isPanelEnabled(panel: TablePanels.ModuleKey) {
  return (
    (store.getters as unknown as GenericObject)[
      `tablePanels/${panel}/enabled`
    ] !== false
  );
}

const viewModeCacheId = computed(() => {
  return props.id ? `${CACHE_ID_PREFIX}.${props.id}.viewMode` : null;
});

const viewMode = ref<ViewMode>(getViewMode());

const viewOptions = [
  { label: 'Single Table View', icon: faGrid2, value: 'single' },
  { label: 'Multi Table View', icon: faList, value: 'multi' },
] as const;

watchEffect(() => {
  if (viewModeCacheId.value) {
    ls.set(viewModeCacheId.value, viewMode.value);
  }
});

const isAdmin = computed(() => {
  return store.state.me.selectedRole?.roleId === 9;
});

const tableCacheId = computed(() => {
  return props.id ? `${CACHE_ID_PREFIX}.${props.id}.table` : null;
});

const selectedTable = ref(getLastSelectedTable());

watchEffect(() => {
  if (tableCacheId.value && selectedTable.value) {
    ls.set(tableCacheId.value, selectedTable.value);
  }
});

const tableOptions = computed(() => {
  return sortBy(configs.value, 'tableKey').map(({ tableKey }) => ({
    text: titleCase(tableKey),
    value: tableKey,
  }));
});

//#region Helper Functions

function isTableKey(value: unknown): value is TablePanels.ModuleKey {
  return isString(value) && value in TablePanels.modules;
}

function getLastSelectedTable() {
  let value: unknown = null;

  if (tableCacheId.value) {
    value = ls.get(tableCacheId.value);
  } else {
    value = configs.value[0]?.tableKey;
  }

  return isTableKey(value) ? value : null;
}

function getViewMode() {
  let value: string | null = null;

  if (viewModeCacheId.value) {
    value = ls.get(viewModeCacheId.value);
  }

  return (
    value === 'single' || value === 'multi' ? value : 'multi'
  ) as ViewMode;
}

//#endregion Helper Functions

return { __sfc: true,CACHE_ID_PREFIX, titleCase, props, store, configs, isPanelEnabled, viewModeCacheId, viewMode, viewOptions, isAdmin, tableCacheId, selectedTable, tableOptions, isTableKey, getLastSelectedTable, getViewMode, Icon, GroupItem, GroupView }
}

})